import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    DatePicker,
    Form,
    Modal,
    Select,
    Space,
    Steps,
    Table,
    Upload,
    message,
} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {createSpecificPush, downloadRosterTemplate, validateRoster} from '@/api/hrPush';
import {getHrTemplates} from '@/api/hrTemplate';
import {getAuthorizedSpecificTypes, SPECIFIC_TYPES} from '../routineUtils';
import {useAuthStore} from '@/context/auth';

const ROSTER_FIELD_REQUIREMENTS = {
    ANNUAL_LEAVE:
        'employee_number（员工号）、until_date（截止日期，格式 yyyy-MM-dd 或 yyyy/MM/dd）',
    REFERRAL_AWARD:
        'employee_number（员工号）、refer_employee_number（推荐人员工号）、bonus（奖金，必须大于 0）',
    POINTS_REMINDER:
        'employee_number（员工号）、points_expire_date（积分截止日，格式 yyyy-MM-dd 或 yyyy/MM/dd）、remaining_points（剩余积分，0-2147483647 的整数）',
    OFFBOARDING: 'employee_number（员工号）',
};

const ROSTER_MAX_SIZE = 5 * 1024 * 1024;

const rosterFormatError = taskType =>
    `上传文件格式错误，请使用 XLSX 文件并确保包含以下字段：${
        ROSTER_FIELD_REQUIREMENTS[taskType] || ROSTER_FIELD_REQUIREMENTS.OFFBOARDING
    }`;

export default function CreatePushModal({open, appId, onClose, onSaved}) {
    const [authStore] = useAuthStore();
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [taskType, setTaskType] = useState();
    const [templates, setTemplates] = useState([]);
    const [roster, setRoster] = useState(null);
    const [pushDate, setPushDate] = useState(null);
    const [saving, setSaving] = useState(false);
    const authorizedTypes = getAuthorizedSpecificTypes(authStore);

    useEffect(() => {
        if (!open) {
            setStep(0);
            setTaskType(null);
            setRoster(null);
            setPushDate(null);
            form.resetFields();
            return;
        }
    }, [open, form]);

    useEffect(() => {
        if (!taskType) return;
        getHrTemplates(appId, {taskType}).then(({data = []}) =>
            setTemplates(data.filter(item => item.enabled))
        );
    }, [appId, taskType]);

    const uploadProps = {
        accept: '.xlsx',
        maxCount: 1,
        beforeUpload: async file => {
            if (!file?.name?.toLowerCase().endsWith('.xlsx')) {
                setRoster(null);
                message.error(rosterFormatError(taskType));
                return false;
            }
            if (file.size > ROSTER_MAX_SIZE) {
                setRoster(null);
                message.error('名单文件不能超过 5MB');
                return false;
            }
            try {
                const {data} = await validateRoster(appId, taskType, file);
                setRoster(data);
                if (data.invalid > 0) message.warning(`名单校验未通过，失败 ${data.invalid} 行`);
            } catch (e) {
                setRoster(null);
                message.error(e?.message || rosterFormatError(taskType));
            }
            return false;
        },
        onRemove: () => setRoster(null),
        fileList: roster
            ? [{uid: 'roster', name: `已校验名单（通过 ${roster.valid} 行）`, status: 'done'}]
            : [],
    };

    const downloadTemplate = async () => {
        try {
            const blob = await downloadRosterTemplate(appId, taskType);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `hr-roster-template-${taskType.toLowerCase()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (e) {
            message.error('名单模板下载失败');
        }
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        const targets = (roster?.rows || [])
            .filter(row => row.valid)
            .map(row => ({
                employeeNumber: row.employeeNumber,
                referEmployeeNumber: row.referEmployeeNumber,
                bonus: row.bonus ? Number(row.bonus) : null,
                untilDate: row.untilDate ? `${row.untilDate}T00:00:00` : null,
                pointsExpireDate: row.pointsExpireDate ? `${row.pointsExpireDate}T00:00:00` : null,
                remainingPoints:
                    row.remainingPoints === null ||
                    row.remainingPoints === undefined ||
                    row.remainingPoints === ''
                        ? null
                        : Number(row.remainingPoints),
                valid: true,
            }));
        setSaving(true);
        try {
            const task = {
                taskName: `${SPECIFIC_TYPES[taskType]}-${dayjs().format('YYYYMMDDHHmm')}`,
                taskType,
                templateId: values.templateId,
                pushDate: pushDate ? pushDate.format('YYYY-MM-DDTHH:mm:ss') : null,
                executeTime: pushDate ? pushDate.format('HH:mm') : null,
                enabled: true,
            };
            await createSpecificPush(appId, {task, targets});
            message.success('创建成功');
            onSaved();
        } catch (e) {
            const reason = e?.message || '创建失败';
            message.error(reason, 8);
            // 后端在"任务已保存、但立即发送失败"时返回该提示：
            // 此时任务已落库，需要关闭弹窗并刷新列表，避免用户以为失败而重复提交（BUG-06）。
            if (reason.includes('任务已创建')) {
                onSaved();
            }
        } finally {
            setSaving(false);
        }
    };

    if (step === 0) {
        return (
            <Modal open={open} title="新建特定推送" footer={null} onCancel={onClose}>
                <div style={{fontWeight: 600, marginBottom: 12}}>特定消息推送-新建推送-1</div>
                <Space direction="vertical" style={{width: '100%'}}>
                    {Object.entries(SPECIFIC_TYPES)
                        .filter(([value]) => authorizedTypes.includes(value))
                        .map(([value, label]) => (
                            <Button
                                key={value}
                                block
                                onClick={() => {
                                    setPushDate(null);
                                    setTaskType(value);
                                    setStep(1);
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                </Space>
            </Modal>
        );
    }

    const canSubmit = !!roster && roster.valid > 0 && roster.invalid === 0;
    return (
        <Modal open={open} title="新建特定推送" footer={null} width={760} onCancel={onClose}>
            <Steps
                current={1}
                items={[{title: '选择类型'}, {title: '配置推送'}]}
                style={{marginBottom: 20}}
            />
            <Form form={form} layout="vertical">
                <Form.Item label="任务类型">
                    <Select
                        value={taskType}
                        disabled
                        options={[{value: taskType, label: SPECIFIC_TYPES[taskType]}]}
                    />
                </Form.Item>
                <Form.Item
                    name="templateId"
                    label="消息模板"
                    rules={[{required: true, message: '请选择消息模板'}]}
                >
                    <Select
                        placeholder="请选择"
                        notFoundContent={
                            templates.length
                                ? '暂无数据'
                                : '该任务类型暂无已启用模板，请先在模板管理中创建并启用'
                        }
                        options={templates.map(({id, templateName}) => ({
                            value: id,
                            label: templateName,
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    label="发送范围"
                    required
                    extra="仅支持 XLSX 文件，单个文件不超过 5MB，名单数据不超过 10000 行；可先点击“名单模板”下载标准格式模板。"
                >
                    <Upload {...uploadProps}>
                        <Button icon={<UploadOutlined />}>上传名单</Button>
                    </Upload>
                    <Button type="link" onClick={downloadTemplate}>
                        名单模板
                    </Button>
                </Form.Item>
                {roster && (
                    <Alert
                        style={{marginBottom: 12}}
                        type={roster.invalid ? 'warning' : 'success'}
                        description={
                            <div>
                                <div style={{marginBottom: 8}}>
                                    {`总数 ${roster.total} / 通过 ${roster.valid} / 失败 ${roster.invalid}`}
                                </div>
                                <Table
                                    size="small"
                                    rowKey="rowNo"
                                    columns={[
                                        {title: '行号', dataIndex: 'rowNo', width: 70},
                                        {
                                            title: '员工号',
                                            dataIndex: 'employeeNumber',
                                            width: 130,
                                        },
                                        ...(taskType === 'ANNUAL_LEAVE'
                                            ? [
                                                  {
                                                      title: '截止日期',
                                                      dataIndex: 'untilDate',
                                                      width: 130,
                                                  },
                                              ]
                                            : []),
                                        ...(taskType === 'POINTS_REMINDER'
                                            ? [
                                                  {
                                                      title: '积分截止日',
                                                      dataIndex: 'pointsExpireDate',
                                                      width: 130,
                                                  },
                                                  {
                                                      title: '剩余积分',
                                                      dataIndex: 'remainingPoints',
                                                      width: 110,
                                                  },
                                              ]
                                            : []),
                                        ...(taskType === 'REFERRAL_AWARD'
                                            ? [
                                                  {
                                                      title: '推荐人员工号',
                                                      dataIndex: 'referEmployeeNumber',
                                                      width: 140,
                                                  },
                                                  {
                                                      title: '奖金',
                                                      dataIndex: 'bonus',
                                                      width: 100,
                                                  },
                                              ]
                                            : []),
                                        {
                                            title: '校验结果',
                                            dataIndex: 'valid',
                                            width: 90,
                                            render: valid => (valid ? '通过' : '失败'),
                                        },
                                        ...(roster.invalid > 0
                                            ? [
                                                  {
                                                      title: '失败原因',
                                                      dataIndex: 'message',
                                                      width: 160,
                                                      render: value => value || '-',
                                                  },
                                              ]
                                            : []),
                                    ]}
                                    dataSource={roster.rows || []}
                                    pagination={{
                                        defaultPageSize: 10,
                                        pageSizeOptions: ['10', '20', '50'],
                                        showSizeChanger: true,
                                        showTotal: total => `共 ${total} 条`,
                                    }}
                                    scroll={{
                                        x:
                                            taskType === 'REFERRAL_AWARD'
                                                ? 760
                                                : taskType === 'POINTS_REMINDER'
                                                ? 730
                                                : 560,
                                        y: 260,
                                    }}
                                />
                            </div>
                        }
                    />
                )}
            </Form>
            <div style={{marginBottom: 24}}>
                <div style={{marginBottom: 8}}>定时发送时间</div>
                <DatePicker
                    allowClear
                    showTime={{format: 'HH:mm', minuteStep: 5}}
                    format="YYYY-MM-DD HH:mm"
                    placeholder="请选择定时发送时间"
                    style={{width: '100%'}}
                    value={pushDate}
                    onChange={setPushDate}
                />
            </div>
            <Space style={{display: 'flex', justifyContent: 'flex-end'}}>
                <Button onClick={() => setStep(0)}>上一步</Button>
                <Button
                    type="primary"
                    disabled={!canSubmit || saving}
                    onClick={submit}
                    loading={saving}
                >
                    {taskType === 'OFFBOARDING' ? '提交审批' : pushDate ? '定时发送' : '立即发送'}
                </Button>
            </Space>
        </Modal>
    );
}

CreatePushModal.propTypes = {
    open: PropTypes.bool.isRequired,
    appId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};
